import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FieldsetModule } from 'primeng/fieldset';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { MessageModule } from 'primeng/message';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ImageModule } from 'primeng/image';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AtividadeService } from '@/features/atividade/atividade/atividade.service';
import { ToiService } from '../toi.service';
import { PdfToiChecklistItem } from '../models/pdf-toi.model';
import { ToiDetalheUpdatePayload } from '../models/toi-api.model';
import { ToiDetalhe, ToiMidiaItem } from '../models/toi-detalhe.model';
import { getToiMidiaKey } from '../utils/toi-midia.util';
import { ToiDetalheSavedEvent, ToiDetalheSavedMotivo } from '../models/toi-pdf-upload.model';
import {
  checklistToiImagemSrc,
  checklistToiMarcado,
  checklistToiValorTexto,
  isChecklistToiCheckbox,
  isChecklistToiImagem,
  ordenarChecklistToi,
  tipoExibicaoChecklistToi,
  ToiChecklistDisplayTipo,
} from '../utils/toi-checklist-display.util';
import { ContextoCorrecaoEnderecoToi } from '../models/controle-toi.model';
import { exigeTratamentoEndereco } from '../utils/controle-toi.util';
import {
  isAguardandoValidacaoStatus,
  isToiStatusSomenteLeitura,
  normalizeToiStatusLabel,
} from '../utils/toi-status.util';
import { usuarioPodeEditarToi } from '../utils/toi-permissao.util';
import { UserService } from '@/core/service/user.service';
import { SafeHtmlPipe } from '@/shared/pipes/safe-html.pipe';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'mf-toi-detalhe',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FieldsetModule,
    ButtonModule,
    TableModule,
    ToastModule,
    MessageModule,
    InputTextModule,
    TooltipModule,
    ConfirmDialogModule,
    ToggleSwitchModule,
    ImageModule,
    SafeHtmlPipe,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './toi-detalhe.component.html',
  styleUrl: './toi-detalhe.component.scss',
})
export class ToiDetalheComponent implements OnInit, OnChanges {
  @Input() detalhe: ToiDetalhe | null = null;
  @Input() loading = true;
  @Input() contextoCorrecaoEndereco: ContextoCorrecaoEnderecoToi | null = null;

  @Output() readonly backRequested = new EventEmitter<void>();
  @Output() readonly pdfRequested = new EventEmitter<void>();
  @Output() readonly saved = new EventEmitter<ToiDetalheSavedEvent>();
  @Output() readonly midiasOcultasChange = new EventEmitter<Set<string>>();

  editing = false;
  saving = false;
  validando = false;
  editForm: FormGroup | null = null;
  ocultasPdf = new Set<string>();
  private modoCorrecaoEnderecoAtivo = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly toiService: ToiService,
    private readonly atividadeService: AtividadeService,
    private readonly messageService: MessageService,
    private readonly confirmationService: ConfirmationService,
    private readonly route: ActivatedRoute,
    private readonly userService: UserService
  ) {}

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['detalhe']) {
      this.inicializarMidiasOcultasPorPadrao();    
    }
    if (changes['detalhe'] || changes['contextoCorrecaoEndereco'] || changes['loading']) {
      this.tentarIniciarEdicaoCorrecaoEndereco();
    }
  }
  
  private inicializarMidiasOcultasPorPadrao(): void {
    const midias = this.detalhe?.midias ?? [];
    this.ocultasPdf = new Set(midias.map((m,i) => getToiMidiaKey(m, i)));
    this.midiasOcultasChange.emit(new Set(this.ocultasPdf));
  }

  get permiteEdicaoUsuario(): boolean {
    return usuarioPodeEditarToi((role) => this.userService.hasPermission(role));
  }

  get modoCorrecaoEndereco(): boolean {
    return this.modoCorrecaoEnderecoAtivo;
  }

  get exigeCorrecaoEndereco(): boolean {
    return (
      !!this.contextoCorrecaoEndereco ||
      exigeTratamentoEndereco(this.detalhe)
    );
  }

  get mensagemErroEndereco(): string | null {
    const ctx =
      this.contextoCorrecaoEndereco?.texto?.trim() ||
      this.contextoCorrecaoEndereco?.mensagemErro?.trim();
    if (ctx) {
      return ctx;
    }
    const retorno = this.detalhe?.dsRetornoCorreios?.trim();
    if (retorno) {
      return retorno;
    }
    return this.detalhe?.dsObservacaoTratamento?.trim() || null;
  }

  get edicaoBloqueada(): boolean {
    if (!this.permiteEdicaoUsuario) {
      return true;
    }

    if (this.modoCorrecaoEndereco) {
      return false;
    }

    const entregueMaos = this.detalhe?.flagEntregueMaos === true;
    const naoPermiteStatus = this.detalhe?.flagPermiteEdicao === false;
    const statusSomenteLeitura = isToiStatusSomenteLeitura(this.statusToiAtual);

    return entregueMaos || naoPermiteStatus || statusSomenteLeitura;
  }

  get enderecoUnidadeUsuariaControl(): FormControl<string> {
    return this.editForm!.get('enderecoUnidadeUsuaria') as FormControl<string>;
  }

  /** Status atual derivado do primeiro evento em eventosToi (mesma regra do Controle TOI). */
  get statusToiAtual(): string {
    const status = this.detalhe?.eventosToi?.[0]?.status?.trim();
    if (status) {
      return normalizeToiStatusLabel(status) ?? status;
    }
    return normalizeToiStatusLabel(this.detalhe?.dsStatusToi) ?? 'TOI Gerado';
  }

  /** Fotos do TOI ainda não sincronizadas pelo mobile. Apenas informativo: não bloqueia a aprovação. */
  get possuiFotosPendentesSincronizacao(): boolean {
    return this.detalhe?.flagSincronizadoMobile === false;
  }

  get podeValidar(): boolean {
    return (
      this.permiteEdicaoUsuario &&
      !!this.detalhe?.idAtividadeToi &&
      isAguardandoValidacaoStatus(this.statusToiAtual)
    );
  }

  get checklistItensOrdenados(): PdfToiChecklistItem[] {
    return ordenarChecklistToi(this.detalhe?.checklistToi);
  }

  tipoChecklistItem(item: PdfToiChecklistItem): ToiChecklistDisplayTipo {
    return tipoExibicaoChecklistToi(item);
  }

  isChecklistMarcado(item: PdfToiChecklistItem): boolean {
    return checklistToiMarcado(item);
  }

  checklistImagemSrc(item: PdfToiChecklistItem): string | null {
    return checklistToiImagemSrc(item);
  }

  checklistValorTexto(item: PdfToiChecklistItem): string {
  return checklistToiValorTexto(item, {
    checklist: this.detalhe?.checklistToi,
    localizacaoAutomatica: this.detalhe?.localizacaoPadrao,
    });
  }

  isMidiaOculta(midia: ToiMidiaItem, index?: number): boolean {
    return this.ocultasPdf.has(getToiMidiaKey(midia, index));
  }

  isMidiaIncluirNoPdf(midia: ToiMidiaItem, index?: number): boolean {
    return !this.isMidiaOculta(midia, index);
  }

  /** URL usada na visualização ampliada da miniatura (preferindo a imagem em tamanho original). */
  midiaUrlAmpliada(midia: ToiMidiaItem): string {
    return midia.downloadUrl || midia.urlThumbnail || '';
  }

  onCorrigirEndereco(): void {
    if (!this.detalhe || !this.permiteEdicaoUsuario || !this.exigeCorrecaoEndereco) {
      return;
    }
    this.iniciarEdicaoCorrecaoEndereco();
  }

  onEditar(): void {
    if (!this.detalhe || this.edicaoBloqueada) {
      return;
    }
    this.modoCorrecaoEnderecoAtivo = false;
    this.editing = true;
    this.editForm = this.buildEditForm(this.detalhe);
    this.enderecoUnidadeUsuariaControl.setValue(
      this.detalhe.enderecoUnidadeUsuaria ?? '',
      { emitEvent: false }
    );
  }

  onCancelar(): void {
    this.editing = false;
    this.saving = false;
    this.editForm = null;
    this.modoCorrecaoEnderecoAtivo = false;
  }

  onSalvar(): void {
    if (!this.permiteEdicaoUsuario || !this.editForm || !this.detalhe) {
      return;
    }
    this.editForm.markAllAsTouched();
    if (this.editForm.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Campos obrigatórios devem ser preenchidos.',
        life: 4000,
      });
      return;
    }

    const isCorrecao = this.modoCorrecaoEndereco;
    this.confirmationService.confirm({
      header: isCorrecao ? 'Confirmar correção de endereço' : 'Confirmação',
      message: isCorrecao
        ? 'Confirmar correção de endereço e reencaminhar para envio?'
        : 'Você tem certeza de que deseja alterar o registro?',
      icon: 'pi pi-question-circle',
      acceptLabel: isCorrecao ? 'Confirmar' : 'OK',
      rejectLabel: 'CANCELAR',
      acceptIcon: 'none',
      rejectIcon: 'none',
      accept: () => (isCorrecao ? this.executarCorrecaoEndereco() : this.executarSalvar()),
    });
  }

  setMidiaIncluirNoPdf(midia: ToiMidiaItem, incluir: boolean, index?: number): void {
    const id = getToiMidiaKey(midia, index);
    if (incluir) {
      this.ocultasPdf.delete(id);
    } else {
      this.ocultasPdf.add(id);
    }
    this.midiasOcultasChange.emit(new Set(this.ocultasPdf));
  }

  onPdf(): void {
    this.pdfRequested.emit();
  }

  onVoltar(): void {
    this.backRequested.emit();
  }

  private tentarEntrarModoCorrecaoAutomatico(): void {
    if (!this.detalhe || this.loading || this.editing) {
      return;
    }

    if (!this.contextoCorrecaoEndereco && !exigeTratamentoEndereco(this.detalhe)) {
      return;
    }

    if (!this.permiteEdicaoUsuario) {
      return;
    }

    this.iniciarEdicaoCorrecaoEndereco();
  }

  onAprovar(): void {
    if (!this.podeValidar) {
      return;
    }

    this.confirmationService.confirm({
      header: 'Aprovar TOI',
      message: `Deseja aprovar o TOI da OS ${this.detalhe!.numeroOS}?`,
      icon: 'pi pi-check-circle',
      acceptLabel: 'Aprovar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-primary',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => this.executarValidacao(true),
    });
  }

  onReprovar(): void {
    if (!this.podeValidar) {
      return;
    }

    this.confirmationService.confirm({
      header: 'Reprovar TOI',
      message: `Deseja reprovar o TOI da OS ${this.detalhe!.numeroOS}?`,
      icon: 'pi pi-times-circle',
      acceptLabel: 'Reprovar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => this.executarValidacao(false),
    });
  }

  private executarValidacao(validacao: boolean): void {
    this.validando = true;
    this.toiService
      .validarToi(this.detalhe!.idAtividadeToi!, validacao)
      .pipe(finalize(() => (this.validando = false)))
      .subscribe({
        next: (res) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail:
              res.mensagemRetorno ||
              (validacao ? 'TOI aprovado com sucesso.' : 'TOI reprovado com sucesso.'),
            life: 4000,
          });
          this.saved.emit({ motivo: validacao ? 'aprovacao' : 'reprovacao' });
        },
        error: (err: Error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: err.message || 'Não foi possível validar o TOI.',
            life: 5000,
          });
        },
      });
  }

  private iniciarEdicaoCorrecaoEndereco(): void {
    if (!this.detalhe) {
      return;
    }
    this.modoCorrecaoEnderecoAtivo = true;
    this.editing = true;
    this.editForm = this.buildEditForm(this.detalhe);
    this.enderecoUnidadeUsuariaControl.setValue(
      this.detalhe.enderecoUnidadeUsuaria ?? '',
      { emitEvent: false }
    );
  }

  private resolveTextoCorrecaoEndereco(): string {
    return (
      this.contextoCorrecaoEndereco?.texto?.trim() ||
      this.contextoCorrecaoEndereco?.mensagemErro?.trim() ||
      this.mensagemErroEndereco ||
      ''
    );
  }

  private executarCorrecaoEndereco(): void {
    if (!this.editForm || !this.detalhe?.idAtividadeToi) {
      return;
    }

    const endereco = String(this.editForm.getRawValue().enderecoUnidadeUsuaria ?? '').trim();
    this.saving = true;
    this.toiService
      .correcaoEnderecoToi(this.detalhe.idAtividadeToi, {
        endereco,
        texto: this.resolveTextoCorrecaoEndereco(),
      })
      .subscribe({
        next: (res) => {
          this.saving = false;
          this.editing = false;
          this.editForm = null;
          this.modoCorrecaoEnderecoAtivo = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: res.mensagemRetorno || 'Endereço corrigido e TOI reencaminhado para envio.',
            life: 4000,
          });
          this.saved.emit({ motivo: 'correcao-endereco' });
        },
        error: (err: Error) => {
          this.saving = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: err.message || 'Não foi possível corrigir o endereço.',
            life: 5000,
          });
        },
      });
  }

  private executarSalvar(): void {
    if (!this.editForm || !this.detalhe) {
      return;
    }

    this.saving = true;

    if (this.contextoCorrecaoEndereco) {
      this.executarSalvarCorrecaoEndereco();
      return;
    }

    const payload = this.buildPayload(this.detalhe.idAtividade, this.editForm);
    this.toiService.salvarDetalhe(payload).subscribe({
      next: () => this.finalizarSalvarComSucesso('Registro salvo com sucesso.'),
      error: (err) => this.finalizarSalvarComErro(err, 'Erro ao salvar TOI.'),
    });
  }

  private executarSalvarCorrecaoEndereco(): void {
    const idAtividadeToi = this.detalhe!.idAtividadeToi;
    if (!idAtividadeToi) {
      this.saving = false;
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Identificador do TOI não encontrado para correção de endereço.',
        life: 5000,
      });
      return;
    }

    const endereco = this.enderecoUnidadeUsuariaControl.value?.trim() ?? '';
    this.toiService
      .correcaoEnderecoToi(idAtividadeToi, {
        endereco,
        texto: this.resolveTextoCorrecaoEndereco(),
      })
      .subscribe({
        next: () =>
          this.finalizarSalvarComSucesso('Endereço corrigido com sucesso.', 'correcao-endereco'),
        error: (err) => this.finalizarSalvarComErro(err, 'Erro ao corrigir endereço do TOI.'),
      });
  }

  private buildEditForm(detalhe: ToiDetalhe): FormGroup {
    return this.fb.group({
      idAtividadeToi: [detalhe.idAtividadeToi ?? 0, Validators.required],
      enderecoUnidadeUsuaria: [detalhe.enderecoUnidadeUsuaria ?? '', Validators.required],
    });
  }

    private finalizarSalvarComSucesso(
    detail: string,
    motivo: ToiDetalheSavedMotivo = 'edicao'
  ): void {
    this.saving = false;
    this.editing = false;
    this.editForm = null;
    this.modoCorrecaoEnderecoAtivo = false;
    this.messageService.add({
      severity: 'success',
      summary: 'Sucesso',
      detail,
      life: 3000,
    });
    this.saved.emit({ motivo });
  }

  private finalizarSalvarComErro(err: unknown, fallbackDetail: string): void {
    this.saving = false;
    const error = err as { error?: { message?: string }; message?: string };
    const detail = error?.error?.message || error?.message || fallbackDetail;
    this.messageService.add({
      severity: 'error',
      summary: 'Erro',
      detail,
      life: 5000,
    });
  }

  private tentarIniciarEdicaoCorrecaoEndereco(): void {
    if (
      !this.contextoCorrecaoEndereco ||
      !this.detalhe ||
      this.loading ||
      this.editing ||
      this.edicaoBloqueada
    ) {
      return;
    }

    this.iniciarEdicaoCorrecaoEndereco();
  }

  private buildPayload(idAtividade: number, form: FormGroup): ToiDetalheUpdatePayload {
    const value = form.getRawValue();
    const d = this.detalhe!;

    const jsonDadosToiAtualizado = JSON.stringify({
      economiaPorCategorias: d.economiaPorCategorias,
      irregularidades: d.irregularidades,
    });

    const agora = new Date().toISOString();
    const usuarioLogado = this.userService.getUserName() || 'SISTEMA';

    return {
      idAtividadeToi: value.idAtividadeToi,
      idAtividade: idAtividade,
      idAtividadeToiStatus: d.idAtividadeToiStatus,
      idChecklist: d.idChecklist,
      idFormulario: d.idFormulario,

      nrDocumento: d.numeroDocumento,
      cdModeloToi: d.cdModeloToi,
      tpToi: d.tpToi || '',
      numeroBa: d.numeroOS,
      cdServico: d.cdServico || '',
      dsTipoInfracao: d.dsTipoInfracao || '',

      flagGerado: d.flagGerado,
      flagEntregueMaos: d.flagEntregueMaos,
      flagRecusaRecebimento: d.flagRecusaRecebimento,
      flagPermiteEdicao: d.flagPermiteEdicao,
      flagSincronizadoMobile: d.flagSincronizadoMobile,
      flagExigeEnvioCorreios: d.flagExigeEnvioCorreios,
      flagEnderecoPendenteTratamento: d.flagEnderecoPendenteTratamento,

      jsonDadosToi: jsonDadosToiAtualizado,
      jsonControleFotos: d.jsonControleFotos,

      dataVistoria: d.dataVistoria,
      dataGeracao: d.dataGeracao,
      dsGerenciaRegional: d.gerenciaRegional,
      dsLocalidade: d.localidade,
      dsEnderecoUnidade: value.enderecoUnidadeUsuaria,
      nrMatriculaImovel: d.matriculaImovel,
      nmUsuarioCadastrado: d.usuarioCadastrado,
      nrIdentificadorUsuario: d.identificadorUsuario,

      economiaPorCategorias: d.economiaPorCategorias ?? [],
      irregularidades: d.irregularidades,

      nrLoteCorreios: d.nrLoteCorreios,
      nrAr: d.nrAr,
      cdRastreio: d.cdRastreio,
      dsRetornoCorreios: d.dsRetornoCorreios,
      dsObservacaoTratamento: d.dsObservacaoTratamento,

      dataEntregaMaos: d.dataEntregaMaos,
      dataValidacao: d.dataValidacao,
      cdUsuarioValidacao: d.cdUsuarioValidacao,
      dataAprovacao: d.dataAprovacao,
      cdUsuarioAprovacao: d.cdUsuarioAprovacao,
      dataEnvioCorreios: d.dataEnvioCorreios,
      dataPostagem: d.dataPostagem,
      dataEntregaCorreios: d.dataEntregaCorreios,
      dataUltimaOcorrencia: d.dataUltimaOcorrencia,

      dataCadastro: d.dataCadastro,
      usuarioCadastro: d.usuarioCadastro,
      dataAtualizacao: agora,
      usuarioAtualizacao: usuarioLogado,
      cdResponsavelEquipe: d.cdResponsavelEquipe,
      nmResponsavelEquipe: d.nmResponsavelEquipe,

      dataUltimaAlteracao: agora,
      cdUsuarioUltimaAlteracao: usuarioLogado,
    };
  }
}
